// What the map was doing when it died.
//
// `Map Crash` (2026-09-11) recorded WHICH map and roughly what — enough to stop
// a crash being a screenshot, not enough to act on. Randy's ask, after the first
// one: a crash on the boat should be a complete report without him taking notes.
//
// The problem is that the two halves live in different components. The error
// boundary is in `_LazyMap`; the zoom, the mode and the scheme are inside the
// map that just threw, and asking a component that is mid-throw for its state
// is not a thing React will do. So the map PUBLISHES its state here as it
// changes, and the boundary READS the last known values at catch time. That is
// also why this is a module-level store rather than a hook or a context: at the
// moment it is read, the subtree that would have provided it is gone.
//
// Everything leaves as a BUCKET. The rule this file inherits from _analytics.js
// is that a cookieless aggregate tracker must not carry a per-visit record, and
// an exact zoom beside an exact age and a timestamp is one. Buckets answer the
// questions actually being asked — "is it always z13?", "how long does it take?"
// — without reconstructing a visit.

// z13 keeps a bucket of its own because that is where Randy saw it, and a
// bucket that contains the answer and its neighbours cannot confirm it.
export const ZOOM_BUCKETS = ['<8', '8-10', '11-12', '13', '14', '15+']

export function zoomBucket(zoom) {
  // `Number(null)` is 0 and `Number('')` is 0, so a zoom that was never
  // published would sail through Number.isFinite and report as "<8" — a bucket
  // that looks like a measurement. Caught by a test that read "<8" out of a
  // map which had never moved.
  if (zoom === null || zoom === undefined || zoom === '') return null
  const z = Number(zoom)
  if (!Number.isFinite(z)) return null
  if (z < 8) return '<8'
  if (z < 11) return '8-10'
  if (z < 13) return '11-12'
  if (z < 14) return '13'
  if (z < 15) return '14'
  return '15+'
}

// Minutes the map had been alive. "a couple of minutes of panning" is the whole
// shape of the report, so the first few minutes get the resolution.
export const AGE_BUCKETS = ['<1', '1-2', '3-5', '6-10', '10+']

export function ageBucket(ms) {
  if (ms === null || ms === undefined || ms === '') return null
  const minutes = Number(ms) / 60000
  if (!Number.isFinite(minutes) || minutes < 0) return null
  if (minutes < 1) return '<1'
  if (minutes < 3) return '1-2'
  if (minutes < 6) return '3-5'
  if (minutes < 11) return '6-10'
  return '10+'
}

// The map state a crash carries. Widened 2026-09-11 with the layers redesign:
// the state is a GROUND and at most one VISUALIZATION, so the bounded set is
// the ten combinations rather than the old four modes. Written out rather than
// generated from _exploreLayers, because the point of this list is to be the
// place cardinality is decided — a set computed from another module can grow
// without anyone reviewing it.
export const MAP_MODES = [
  'standard',
  'standard+tides',
  'standard+currents',
  'standard+wind',
  'standard+radar',
  'chart',
  'chart+tides',
  'chart+currents',
  'chart+wind',
  'chart+radar',
  // The legacy vocabulary, still emitted by the station and home maps until
  // they adopt the sheet (Phase 3).
  'tides',
  'currents',
  'depth',
  'radar',
  'none',
]
export const MAP_SCHEMES = ['light', 'dark']

// Live state, replaced as the map moves. Deliberately plain: no subscribers, no
// events, nothing that could itself throw while a boundary is unwinding.
let state = {
  zoom: null,
  mode: null,
  scheme: null,
  liveSince: null,
  glLost: false,
}

// Called by the map as its view changes. Cheap enough to call from a move
// handler: three assignments and no allocation.
export function setMapContext({ zoom, mode, scheme } = {}) {
  if (zoom !== undefined) state.zoom = zoom
  if (mode !== undefined) state.mode = mode
  if (scheme !== undefined) state.scheme = scheme
}

// The map is drawing. Starts the age clock — which is the clock that matters,
// not page load: the report is about how long the MAP survived.
export function noteMapLive(now = Date.now()) {
  if (state.liveSince === null) state.liveSince = now
}

// LATCHED, and that is the point. On iOS the context can go away without the
// resulting throw mentioning WebGL at all — an allocation failure in a render
// reads as an ordinary TypeError — so `reason` alone cannot tell a GPU death
// from a bug in our code. This flag is the only thing that can, and it must
// survive until the crash rather than being cleared by a restore.
export function noteContextLost() {
  state.glLost = true
}

export function resetMapContext() {
  state = { zoom: null, mode: null, scheme: null, liveSince: null, glLost: false }
}

// The props for a crash, all bounded, all optional. A value that was never
// published is OMITTED rather than sent as a guess: "unknown" in a bucket
// column is indistinguishable from a real bucket when someone reads the chart
// later, and an absent property is honest about what was not known.
export function mapCrashContext(now = Date.now()) {
  const props = {}
  const zoom = zoomBucket(state.zoom)
  if (zoom) props.zoom = zoom

  if (state.liveSince !== null) {
    const age = ageBucket(now - state.liveSince)
    if (age) props.age = age
  }

  if (MAP_MODES.includes(state.mode)) props.mode = state.mode
  if (MAP_SCHEMES.includes(state.scheme)) props.scheme = state.scheme
  // Always sent, because "no" is a finding: a crash with no context loss behind
  // it is our bug, and an absent property would read as "not measured".
  props.gl_lost = state.glLost ? 'yes' : 'no'
  return props
}

// ---- The crash nobody sees ------------------------------------------------
//
// iOS can kill the whole tab rather than just the GL context. The page reloads
// itself, no boundary catches anything, and no event fires — the only sign is
// that the map is suddenly back at its starting view. Randy has been told to
// watch for exactly that, which is not a reporting system.
//
// A reload cannot be distinguished from a manual one by the navigation type
// alone: both read as `reload`. What DOES separate them is that a clean exit
// runs `pagehide` and a process kill does not. So the page leaves two marks —
// "a map was live here" and, on the way out, "I left cleanly" — and a load that
// finds the first without the second is a tab that did not get to say goodbye.
//
// Stated plainly because it will be read off a chart later: this is EVIDENCE,
// not proof. A crashed browser, a force-quit, or a device reboot all look the
// same. What makes it worth having is that the alternative is nothing at all.
const LIVE_KEY = 'tnm_map_live'
const CLEAN_KEY = 'tnm_clean_exit'

const safeSession = () => {
  try {
    // Private mode and disabled storage both throw on ACCESS, not just on write.
    return window.sessionStorage ?? null
  } catch {
    return null
  }
}

// Call when a map starts drawing. Marks the tab and arms the clean-exit hook.
export function armReloadDetector(win = typeof window === 'undefined' ? null : window) {
  const store = safeSession()
  if (!win || !store) return () => {}
  try {
    store.setItem(LIVE_KEY, '1')
    store.removeItem(CLEAN_KEY)
  } catch {
    return () => {}
  }
  const onPageHide = () => {
    try {
      store.setItem(CLEAN_KEY, '1')
    } catch {
      // Nothing to do; the next load simply reads it as unclean.
    }
  }
  win.addEventListener('pagehide', onPageHide)
  return () => win.removeEventListener('pagehide', onPageHide)
}

// Call once on load, BEFORE arming. Returns 'unclean' when this looks like a
// tab that was killed while a map was live, null otherwise. Clears the marks
// either way, so one kill is reported once.
export function detectUncleanReload(win = typeof window === 'undefined' ? null : window) {
  const store = safeSession()
  if (!win || !store) return null
  let wasLive
  let cleanExit
  try {
    wasLive = store.getItem(LIVE_KEY)
    cleanExit = store.getItem(CLEAN_KEY)
    store.removeItem(LIVE_KEY)
    store.removeItem(CLEAN_KEY)
  } catch {
    return null
  }
  if (!wasLive || cleanExit) return null

  // A fresh tab has no marks at all, so reaching here already means the same
  // tab ran a map. The navigation type narrows it to a reload rather than a
  // forward navigation that happens to render another map.
  const nav = win.performance?.getEntriesByType?.('navigation')?.[0]
  if (nav && nav.type !== 'reload') return null
  return 'unclean'
}
